import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Download, FileText, Filter, Folder, Lock, ShieldCheck, PenLine } from "lucide-react";
import { ManualBankReadOnly } from "@/components/ManualBankReadOnly";
import SignDocumentModal from "@/components/SignDocumentModal";
import OfficeManagerFeed from "@/components/OfficeManagerFeed";
import RemoteTrackingGate from "@/components/RemoteTrackingGate";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const DOC_KIND_LABELS = {
  application: "Application",
  certification: "Certification",
  attestation: "Attestation",
  education: "Education Records",
  i9: "Form I-9",
  w4: "Form W-4",
  direct_deposit: "Direct Deposit Auth",
  id: "ID / License",
  handbook: "Handbook Ack.",
  other: "Other",
};

export default function EmployeePortal() {
  const { user } = useAuth();
  const [stubs, setStubs] = useState([]);
  const [taxDocs, setTaxDocs] = useState([]);
  const [hrDocs, setHrDocs] = useState([]);
  const [probation, setProbation] = useState(null);
  const [me, setMe] = useState(null);
  const [year, setYear] = useState("all");
  const [month, setMonth] = useState("all");
  const [sortDir, setSortDir] = useState("desc");
  const [signing, setSigning] = useState(null);  // { id, filename } while modal open
  const [stubSelfService, setStubSelfService] = useState(null);

  const reloadHr = async () => {
    const { data } = await api.get("/me/hr-documents");
    setHrDocs(data?.documents || []);
    setProbation(data?.probation || null);
  };

  useEffect(() => {
    // Load once on mount — endpoints auto-scope to the current employee via cookie.
    (async () => {
      const [s, t, h, m] = await Promise.all([
        api.get("/paystubs"),
        api.get("/tax-documents"),
        api.get("/me/hr-documents"),
        api.get("/me/employee").catch(() => ({ data: null })),
      ]);
      setStubs(s.data);
      setTaxDocs(t.data);
      setHrDocs(h.data?.documents || []);
      setProbation(h.data?.probation || null);
      setMe(m.data);
    })();
    api.get("/config/features")
      .then(({ data }) => setStubSelfService(!!data.employee_stub_download))
      .catch((err) => { console.warn("feature flags unavailable", err); setStubSelfService(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { years, filtered } = useMemo(() => {
    const yrs = Array.from(new Set(stubs.map((s) => (s.pay_date || "").slice(0, 4)).filter(Boolean))).sort().reverse();
    let rows = stubs.slice();
    if (year !== "all") rows = rows.filter((s) => (s.pay_date || "").startsWith(year));
    if (month !== "all") rows = rows.filter((s) => (s.pay_date || "").slice(5, 7) === month);
    rows.sort((a, b) => (a.pay_date < b.pay_date ? 1 : -1) * (sortDir === "desc" ? 1 : -1));
    return { years: yrs, filtered: rows };
  }, [stubs, year, month, sortDir]);

  const ytd = stubs.reduce((acc, s) => ({
    gross: acc.gross + (s.gross || 0),
    net: acc.net + (s.net_pay || 0),
    fed: acc.fed + (s.federal_tax || 0),
    ss: acc.ss + (s.social_security || 0),
    med: acc.med + (s.medicare || 0),
    state: acc.state + (s.state_tax || 0),
  }), { gross: 0, net: 0, fed: 0, ss: 0, med: 0, state: 0 });

  return (
    <RemoteTrackingGate>
    <div className="p-10">
      <div className="overline mb-2">§ EMPLOYEE PORTAL</div>
      <h1 className="font-heading font-black text-5xl tracking-tight mb-2">Hello, {user?.name?.split(" ")[0]}</h1>
      <p className="text-muted-foreground mb-8">
        {stubSelfService
          ? "View & download your pay stubs and tax documents."
          : "Your timesheets, time off, and tax documents. Pay stubs are issued by your administrator."}
      </p>

      <OfficeManagerFeed scope="employee" />

      {/* YTD is derived from the stub ledger, so it is hidden alongside it.
          Nothing renders until the flag resolves — no $0.00 flash. */}
      {stubSelfService === true && (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="portal-ytd-cards">
        <Card label="YTD Gross" val={ytd.gross} />
        <Card label="YTD Net" val={ytd.net} color="text-good" />
        <Card label="YTD Federal Tax" val={ytd.fed} color="text-bad" />
        <Card label="YTD FICA + State" val={ytd.ss + ytd.med + ytd.state} color="text-bad" />
      </div>
      )}


      {/* PAY STUBS SECTION */}
      {stubSelfService === null ? null : !stubSelfService ? (
        <section className="mb-14">
          <div className="overline mb-1">§ PAY STUBS</div>
          <h2 className="font-heading font-black text-3xl tracking-tight mb-4">Pay Stubs</h2>
          <div className="hard-card p-6 max-w-2xl" data-testid="portal-stubs-locked">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 mt-0.5 shrink-0" />
              <div>
                <div className="font-heading font-black text-base md:text-lg">Issued by your administrator</div>
                <p className="text-sm mt-2 text-muted-foreground">
                  Pay stub history and PDF downloads are handled by your payroll administrator.
                  Each stub is emailed to you the moment it is released — contact your admin if you
                  need a copy of an earlier period.
                </p>
              </div>
            </div>
          </div>
        </section>
      ) : (
      <section className="mb-14">
        <div className="flex items-end justify-between mb-4 gap-4 flex-wrap">
          <div>
            <div className="overline mb-1">§ PAY STUBS · PAST 12 MONTHS</div>
            <h2 className="font-heading font-black text-3xl tracking-tight">Pay Stubs</h2>
          </div>
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-widest">
            <Filter className="w-4 h-4" />
            <label className="flex items-center gap-2">
              <span>Year</span>
              <select data-testid="filter-year" value={year} onChange={(e) => setYear(e.target.value)} className="border-2 border-ink bg-white px-2 py-1">
                <option value="all">All</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2">
              <span>Month</span>
              <select data-testid="filter-month" value={month} onChange={(e) => setMonth(e.target.value)} className="border-2 border-ink bg-white px-2 py-1">
                <option value="all">All</option>
                {MONTH_NAMES.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
              </select>
            </label>
            <button
              data-testid="toggle-sort"
              onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
              className="border-2 border-ink px-3 py-1 hover:bg-ink hover:text-white transition-colors"
            >
              {sortDir === "desc" ? "Newest ↓" : "Oldest ↑"}
            </button>
          </div>
        </div>

        <div className="hard-card overflow-x-auto">
          <table className="w-full font-mono text-sm">
            <thead className="bg-ink text-white">
              <tr>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Pay Date</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Period</th>
                <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Gross</th>
                <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Taxes</th>
                <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Net</th>
                <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-muted-foreground">No pay stubs match this filter.</td></tr>}
              {filtered.map((s, i) => (
                <tr key={s.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F9F9F9]"} data-testid={`stub-row-${s.id}`}>
                  <td className="px-4 py-3 border-t border-ink/20 font-semibold">{s.pay_date}</td>
                  <td className="px-4 py-3 border-t border-ink/20">{s.period_start} → {s.period_end}</td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right">{fmtMoney(s.gross)}</td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right text-bad">{fmtMoney(s.total_deductions)}</td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right font-black">{fmtMoney(s.net_pay)}</td>
                  <td className="px-4 py-3 border-t border-ink/20 text-right space-x-2 whitespace-nowrap">
                    <Link to={`/app/paystubs/${s.id}`} className="border border-ink px-2 py-1 text-xs uppercase tracking-widest hover:bg-ink hover:text-white">View</Link>
                    <a data-testid={`download-pdf-${s.id}`} href={`${API}/paystubs/${s.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 bg-ink text-white px-2 py-1 text-xs uppercase tracking-widest">
                      <Download className="w-3 h-3" /> PDF
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}


      {/* PLAID BANK VERIFICATION */}
      {me && (
        <section className="mb-8" data-testid="plaid-section">
          <div className="mb-4">
            <div className="overline mb-1">§ DIRECT DEPOSIT</div>
          </div>
          <div className="hard-card p-4 mb-4" data-testid="payout-destination-picker">
            <div className="font-mono text-[11px] text-muted-foreground mb-3">
              Choose where your net pay lands. Instant card delivery arrives in minutes when your
              employer funds payroll with their business card; standard bank deposits are free and
              arrive on the normal ACH schedule.
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { v: "instant_card", l: "Linked Card (Instant)" },
                { v: "standard_bank", l: "Linked Bank (Standard)" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  data-testid={`payout-dest-${opt.v}`}
                  onClick={async () => {
                    try {
                      await api.post("/portal/payout-destination", { payout_destination: opt.v });
                      const { data } = await api.get("/me/employee");
                      setMe(data);
                    } catch (err) { console.warn("payout destination update failed", err); }
                  }}
                  className={`border-2 px-4 py-2 font-mono uppercase text-[10px] tracking-widest ${
                    (me.payout_destination || "standard_bank") === opt.v
                      ? "border-ink bg-ink text-white"
                      : "border-ink hover:bg-ink hover:text-white"
                  }`}
                >
                  {opt.l}
                </button>
              ))}
            </div>
          </div>
          <ManualBankReadOnly me={me} />
        </section>
      )}

      {/* HR DOCUMENTS SECTION (respects 90-day probation) */}
      <section className="mb-14" data-testid="hr-docs-section">
        <div className="mb-4">
          <div className="overline mb-1">§ HR DOCUMENTS</div>
          <h2 className="font-heading font-black text-3xl tracking-tight">Applications · Certifications · Attestations</h2>
        </div>
        {probation && !probation.cleared ? (
          <div className="hard-card p-6 flex items-start gap-4" data-testid="probation-lock">
            <Lock className="w-6 h-6 shrink-0 mt-1 text-warn" />
            <div>
              <div className="font-heading font-black text-lg mb-1">Documents unlock after your 90-day probation</div>
              <div className="font-mono text-xs text-muted-foreground leading-relaxed">
                You've been with us for <b>{probation.days_since_hire}</b> day{probation.days_since_hire === 1 ? "" : "s"} (hired <b>{probation.hire_date}</b>).
                {" "}Your applications, certifications, and attestations become available in <b data-testid="probation-days-left">{probation.days_remaining}</b> more day{probation.days_remaining === 1 ? "" : "s"}. Reach out to HR if you need a copy sooner.
              </div>
            </div>
          </div>
        ) : hrDocs.length === 0 ? (
          <div className="hard-card p-6 text-muted-foreground font-mono text-sm" data-testid="hr-docs-empty">
            No HR documents on file yet — HR will upload your application, certifications, and attestations as they're processed.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {hrDocs.map((d) => (
              <div key={d.id} className="hard-card p-0 flex flex-col overflow-hidden" data-testid={`hr-doc-${d.id}`}>
                <div className="w-full h-40 border-b-2 border-ink bg-secondary/40 flex items-center justify-center overflow-hidden">
                  {d.thumbnail_data_url ? (
                    <img
                      data-testid={`hr-doc-thumb-${d.id}`}
                      src={d.thumbnail_data_url}
                      alt={d.filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Folder className="w-12 h-12 text-ink/40" strokeWidth={1.5} />
                  )}
                </div>
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div>
                    <div className="overline mb-1 flex items-center gap-2">
                      {DOC_KIND_LABELS[d.kind] || d.kind}
                      {d.signed_at && <span className="text-good inline-flex items-center gap-1 text-[10px] normal-case"><ShieldCheck className="w-3 h-3" /> Signed</span>}
                      {d.signature_required && !d.signed_at && <span className="text-warn text-[10px] normal-case">· Signature required</span>}
                    </div>
                    <div className="font-heading font-black text-base leading-tight break-all">{d.filename}</div>
                    <div className="font-mono text-[10px] text-muted-foreground mt-1">
                      {d.size_kb} KB · uploaded {new Date(d.uploaded_at).toLocaleDateString()}
                      {d.signed_at && (<><br />Signed by you on {new Date(d.signed_at).toLocaleDateString()}</>)}
                    </div>
                  </div>
                  <div className="mt-auto space-y-2">
                    {d.signature_required && !d.signed_at && (
                      <button
                        data-testid={`hr-doc-sign-${d.id}`}
                        onClick={() => setSigning(d)}
                        className="hard-btn w-full py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2"
                      >
                        <PenLine className="w-4 h-4" /> Sign & Acknowledge
                      </button>
                    )}
                    <a
                      data-testid={`hr-doc-download-${d.id}`}
                      href={`${API}/me/hr-documents/${d.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      className={`w-full py-2 font-mono text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2 ${d.signature_required && !d.signed_at ? "border-2 border-ink hover:bg-secondary" : "hard-btn"}`}
                    >
                      <Download className="w-4 h-4" /> Download
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {signing && (
        <SignDocumentModal
          doc={signing}
          employeeName={user?.name || ""}
          onClose={() => setSigning(null)}
          onSigned={reloadHr}
        />
      )}

      {/* TAX DOCUMENTS SECTION */}
      <section>
        <div className="mb-4">
          <div className="overline mb-1">§ TAX DOCUMENTS</div>
          <h2 className="font-heading font-black text-3xl tracking-tight">W-2 &amp; Year-End Statements</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {taxDocs.length === 0 && (
            <div className="col-span-full hard-card p-6 text-muted-foreground font-mono text-sm">
              No tax documents available yet.
            </div>
          )}
          {taxDocs.map((d) => (
            <div key={`${d.year}-${d.employee_id}`} className="hard-card hard-card-shadow p-6 flex flex-col gap-4" data-testid={`w2-card-${d.year}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline mb-1">FORM W-2</div>
                  <div className="font-heading font-black text-3xl tracking-tight">{d.year}</div>
                  <div className="font-mono text-xs text-muted-foreground mt-1">{d.stub_count} pay periods · {d.employee_name}</div>
                </div>
                <FileText className="w-8 h-8" strokeWidth={2.25} />
              </div>
              <div className="font-mono text-xs space-y-1 border-t-2 border-ink pt-3">
                <Row label="Wages (Box 1)" val={d.gross} />
                <Row label="Federal Tax (Box 2)" val={d.federal_tax} color="text-bad" />
                <Row label="SS Tax (Box 4)" val={d.social_security} color="text-bad" />
                <Row label="Medicare (Box 6)" val={d.medicare} color="text-bad" />
                <Row label="State Tax (Box 17)" val={d.state_tax} color="text-bad" />
              </div>
              <a data-testid={`download-w2-${d.year}`} href={`${API}/tax-documents/w2/${d.year}/${d.employee_id}/pdf`} target="_blank" rel="noreferrer" className="hard-btn w-full py-2.5 font-mono text-xs uppercase tracking-widest inline-flex items-center justify-center gap-2">
                <Download className="w-4 h-4" /> Download W-2 PDF
              </a>
            </div>
          ))}
        </div>
      </section>
    </div>
    </RemoteTrackingGate>
  );
}

function Card({ label, val, color = "" }) {
  return (
    <div className="hard-card p-4">
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-black text-2xl ${color}`}>{fmtMoney(val)}</div>
    </div>
  );
}

function Row({ label, val, color = "" }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span className={color}>{fmtMoney(val)}</span>
    </div>
  );
}
